/**
 * UID-330 — the main-thread side of thumbnail rendering: a lazily-created single
 * render Web Worker plus small safety guards. A single worker drains requests
 * SEQUENTIALLY, which is the bounded concurrency the plan calls for (a 10-file
 * batch never spawns 10 simultaneous WASM renders and freezes the tab).
 *
 * Sequential draining is ENFORCED here (2026-09-04 review), not just asserted by
 * this comment: at most one `postMessage` is ever in flight — a second request
 * arriving while one is outstanding is queued, not sent. Before this, every call
 * posted immediately; the worker's `onmessage` handler is `async`, so a SECOND
 * message arriving while the first was suspended at an `await` (e.g. the
 * occt-import-js WASM call) would start executing concurrently in the same
 * realm — exactly the "10 simultaneous WASM renders" this module claims to
 * prevent. Queueing on the sender side, not just relying on the worker's single
 * thread, is what actually prevents that interleaving.
 *
 * Everything here is best-effort and side-effect free on failure: any guard trip
 * (no worker support, oversize file, timeout, worker error) resolves to `null`,
 * and the caller keeps the placeholder. Never throws to the caller.
 */

import { type ThumbnailRenderKind, kindForFile } from './kindForFile'
import type { ThumbnailWorkerRequest, ThumbnailWorkerResponse } from './protocol'

/** Skip rendering files larger than this — reading a huge CAD file into the WASM
 * heap risks OOM, and the thumbnail isn't worth it. Placeholder stays. */
const MAX_RENDER_BYTES = 60 * 1024 * 1024 // 60 MiB
/** Cap concurrently-queued jobs so a massive multi-file drop can't pin memory
 * with many buffered ArrayBuffers; extras just keep the placeholder. Counts
 * BOTH the one in-flight job and every job waiting in `queue` — `pending`'s
 * size is exactly that total. */
const MAX_QUEUED = 24
/** A single render must not hang the queue forever (corrupt geometry, WASM stall). */
const RENDER_TIMEOUT_MS = 30_000

interface Pending {
  resolve(blob: Blob | null): void
  /** `null` while the job sits in `queue` — the render timeout only makes sense
   * (and only starts) once the job is actually SENT (see {@link send}). Started
   * at enqueue time instead, a job stuck behind others in a big batch could
   * time out before the worker ever began it. */
  timer: ReturnType<typeof setTimeout> | null
}

let worker: Worker | null = null
let workerUnavailable = false
let nextId = 1
/** True while a request is posted to the worker and its reply hasn't landed yet
 * — the ONE job the worker may be actively rendering. */
let busy = false
const pending = new Map<number, Pending>()
/** Requests waiting for `busy` to clear, FIFO. Each still has its OWN
 * `ArrayBuffer` (from that file's `arrayBuffer()`), so transferring it at SEND
 * time (not creation time) is safe — no job's buffer is touched until it is
 * actually posted. */
const queue: ThumbnailWorkerRequest[] = []

/** True only in a browser that can actually run the worker + OffscreenCanvas
 * pipeline (guards SSR / jsdom / older browsers — tests get `null`, not a throw). */
function canRender(): boolean {
  return (
    typeof Worker !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof URL !== 'undefined'
  )
}

/** Actually post `request` and start ITS render timeout — the timeout is
 * deliberately started HERE, not at creation, so time spent waiting in
 * `queue` behind other jobs never eats into a job's own render budget. */
function send(w: Worker, request: ThumbnailWorkerRequest): void {
  busy = true
  const entry = pending.get(request.id)
  if (entry) {
    entry.timer = setTimeout(() => {
      if (pending.delete(request.id)) {
        entry.resolve(null)
      }
    }, RENDER_TIMEOUT_MS)
  }
  w.postMessage(request, [request.buffer])
}

/** Send the next queued request, if any; otherwise mark the worker free. Called
 * whenever a reply lands — a REAL reply, never a client-side timeout (see
 * {@link send}'s comment for why advancing there would reintroduce the exact
 * interleaving this module exists to prevent). */
function advanceQueue(w: Worker): void {
  const request = queue.shift()
  if (request === undefined) {
    busy = false
    return
  }
  send(w, request)
}

function getWorker(): Worker | null {
  if (workerUnavailable) {
    return null
  }
  if (worker) {
    return worker
  }
  try {
    worker = new Worker(new URL('./renderThumbnail.worker.ts', import.meta.url), { type: 'module' })
    worker.onmessage = (event: MessageEvent<ThumbnailWorkerResponse>) => {
      const { id } = event.data
      const entry = pending.get(id)
      if (entry) {
        if (entry.timer) clearTimeout(entry.timer)
        pending.delete(id)
        entry.resolve(event.data.ok ? new Blob([event.data.png], { type: 'image/png' }) : null)
      }
      // Advance regardless of whether `entry` still existed: a reply for a job
      // that already timed out client-side STILL means the worker is free for
      // the next queued one.
      if (worker) {
        advanceQueue(worker)
      }
    }
    worker.onerror = () => {
      // Fail every outstanding AND queued job closed, then drop the worker so
      // the next request lazily rebuilds it (a single bad render shouldn't
      // kill the feature).
      for (const [, entry] of pending) {
        if (entry.timer) clearTimeout(entry.timer)
        entry.resolve(null)
      }
      pending.clear()
      queue.length = 0
      busy = false
      worker?.terminate()
      worker = null
    }
    return worker
  } catch {
    workerUnavailable = true
    return null
  }
}

/**
 * Render a thumbnail PNG for `file`, or resolve `null` when it can't/shouldn't be
 * rendered (unsupported format, too large, queue full, no worker support, render
 * failure, or timeout). Best-effort — NEVER rejects, so a caller can fire this
 * without guarding the upload flow.
 */
export function renderThumbnailPng(file: File): Promise<Blob | null> {
  const kind: ThumbnailRenderKind | null = kindForFile(file)
  if (kind === null || file.size <= 0 || file.size > MAX_RENDER_BYTES) {
    return Promise.resolve(null)
  }
  if (!canRender() || pending.size >= MAX_QUEUED) {
    return Promise.resolve(null)
  }
  const w = getWorker()
  if (w === null) {
    return Promise.resolve(null)
  }

  return file
    .arrayBuffer()
    .then(
      (buffer) =>
        new Promise<Blob | null>((resolve) => {
          const id = nextId++
          // `timer` starts `null` — {@link send} starts it once this job is
          // actually posted, not while it may still be waiting in `queue`.
          pending.set(id, { resolve, timer: null })
          const request: ThumbnailWorkerRequest = { id, kind, name: file.name, buffer }
          if (busy) {
            queue.push(request)
          } else {
            send(w, request)
          }
        }),
    )
    .catch(() => null)
}
